<script setup>
import { computed } from 'vue';

import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import PortalLayoutFileLogo from "./PortalLayoutFileLogo.vue";
import {isCustomColor} from "../../../../../helpers/common";
import NavigationLinks from "./NavigationLinks.vue";
import MobileAndDesktopProfileDropdown from "./MobileAndDesktopProfileDropdown.vue";
import HeaderBlock from "./HeaderBlock.vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore();
const navigationColor = computed(() => portalContentAndDesignStore.portalLayoutSettings?.portal_layout_navigation_color ?? 'white');
const navigationLinksStyle = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_navigation_links);
const portalLayout = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout);
const primaryColor = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_primary_color);

const navbarBgClass = computed(() => {
    if (portalLayout.value === 'three' || portalLayout.value === 'two') {
        switch (navigationColor.value) {
            case 'white': return 'bg-white border-b border-gray-200 border-opacity-75';
            case 'gray': return 'bg-gray-50 border-b border-gray-200 border-opacity-75';
            case 'black': return 'bg-gray-900';
            default:
                return portalContentAndDesignStore.getNavBackgroundColorClass;
        }
    } else {
        switch (navigationColor.value) {
            case 'white': return 'bg-white';
            case 'gray': return 'bg-gray-100';
            case 'black': return 'bg-gray-900';
            default:
                return portalContentAndDesignStore.getNavBackgroundColorClass;
        }
    }
});

const hamburgerButtonClass = computed(() => {
    if (['white', 'gray'].includes(navigationColor.value)) {
        return 'opacity-60 hover:opacity-80 focus:opacity-100 focus:ring-black';
    }
    return 'text-white opacity-75 hover:opacity-90 focus:opacity-100 focus:ring-white';
});

const sidebarClasses = computed(() => {
    switch (portalLayout.value) {
        case 'three':
            return getSidebarLayoutThreeClasses();
        case 'two':
            return getSidebarLayoutTwoClasses();
        default:
            return getSidebarLayoutOneClasses();
    }
});

function getSidebarLayoutThreeClasses() {

    const sidebarBase = 'hidden lg:flex lg:flex-shrink-0 lg:flex-col';

    if (navigationColor.value === 'white') return `${sidebarBase} bg-white border-r border-gray-200 border-opacity-75`;
    if (navigationColor.value === 'gray') return `${sidebarBase} bg-gray-50 border-r border-gray-200 border-opacity-75`;
    if (navigationColor.value === 'black') return `${sidebarBase} bg-gray-900`;

    return `${sidebarBase} ${isCustomColor(primaryColor.value) ? 'bg-custom-primary' : `bg-${primaryColor.value}`}`;
}

function getSidebarLayoutTwoClasses() {

    if (navigationColor.value === 'brand') {
        return `hidden lg:block border-b border-white border-opacity-10 ${isCustomColor(primaryColor.value) ? 'bg-custom-primary' : `bg-${primaryColor.value}`}`;
    }

    if (navigationColor.value === 'black') {
        return 'hidden lg:block border-b bg-gray-900 border-white border-opacity-10';
    }

    if (navigationColor.value === 'gray') {
        return 'hidden lg:block border-b bg-gray-100 bg-opacity-50 border-gray-200 border-opacity-75';
    }

    return 'hidden lg:block border-b bg-white border-gray-200 border-opacity-75';
}

function getSidebarLayoutOneClasses() {

    if (navigationColor.value === 'white') return 'bg-white pb-24';
    if (navigationColor.value === 'black') return 'bg-gray-900 pb-24';
    if (navigationColor.value === 'gray') return 'bg-gray-100 pb-24';

    return `${isCustomColor(primaryColor.value) ? 'bg-custom-primary' : `bg-${primaryColor.value}`} pb-24`;
}

</script>
<template>
    <hamburger v-slot="{ childScope }">
        <div
            class="block lg:hidden fixed w-full top-0 z-50"
            :class="navbarBgClass"
        >
            <nav :class="portalContentAndDesignStore.getBorderNavigationColorClass">
                <div class="px-6 mx-auto max-w-6xl">
                    <div class="flex relative justify-between items-center h-14 lg:border-indigo-400 lg:border-opacity-10">
                        <div class="flex items-center space-x-4">
                            <!-- Hamburger toggle -->
                            <button
                                type="button"
                                class="inline-flex justify-center items-center w-8 h-8 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-10"
                                :class="hamburgerButtonClass"
                                aria-controls="mobile-menu"
                                aria-expanded="false"
                                @click="childScope.hamburgerActive=!childScope.hamburgerActive"
                            >
                                <span class="sr-only">Open main menu</span>
                                <svg class="w-5 h-5" fill="currentColor"
                                     :class="{ hidden: childScope.hamburgerActive }"
                                     xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24"
                                     height="24">
                                    <path fill="none" d="M0 0h24v24H0z"/>
                                    <path d="M3 4h18v2H3V4zm0 7h12v2H3v-2zm0 7h18v2H3v-2z"/>
                                </svg>
                                <svg class="w-5 h-5" fill="currentColor"
                                     :class="{ hidden: !childScope.hamburgerActive }"
                                     xmlns="http://www.w3.org/2000/svg"
                                     viewBox="0 0 24 24" width="24" height="24">
                                    <path fill="none" d="M0 0h24v24H0z"/>
                                    <path
                                        d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"/>
                                </svg>
                            </button>

                            <portal-layout-file-logo />
                        </div>

                        <mobile-and-desktop-profile-dropdown :is-mobile="true" />
                    </div>
                </div>
            </nav>

            <!-- Mobile menu dropdown -->
            <div
                id="mobile-menu"
                :class="[
                    'border-b', portalContentAndDesignStore.getBorderNavigationColorClass,
                    !childScope.hamburgerActive ? 'hidden' : ''
                ]"
            >
            <div class="py-4 px-6 mx-auto space-y-2 max-w-6xl">
                <navigation-links layout-type="mobile" />
            </div>
        </div>
        </div>

        <!-- Portal layout style THREE -->
        <div v-if="portalLayout === 'three'"
            :class="sidebarClasses"
        >
            <div class="flex flex-col flex-1 py-10 px-8 w-64">
                <portal-layout-file-logo v-if="portalContentAndDesignStore.srcRoutes.portal_layout_file_logo" />
                <RouterLink v-else :to="{ name: 'dashboard' }">
                    <div
                        class="overflow-hidden px-4 text-xl font-semibold whitespace-nowrap overflow-ellipsis"
                        :class="[
                          navigationColor === 'brand' || navigationColor === 'black'
                            ? 'text-white'
                            : portalContentAndDesignStore.getTextColorClass
                        ]"
                    >
                        {{ portalContentAndDesignStore.program?.name }}
                    </div>
                </RouterLink>

                <nav :class="[
                    'flex-1 space-y-4 mt-10',
                    navigationLinksStyle === 'border' ? '-mr-8' : '',
                ]">
                    <navigation-links />
                </nav>
            </div>

            <mobile-and-desktop-profile-dropdown :is-sidebar-menu="true" />

        </div>

        <!-- Portal layout style TWO -->
        <template v-else-if="portalLayout === 'two'">
            <nav :class="[
                    'hidden lg:block border-b',
                    sidebarClasses
                ]"
            >
                <div class="px-4 mx-auto max-w-6xl sm:px-8 lg:px-12">
                    <div class="flex relative justify-between items-center -mb-px h-16">
                        <div class="flex items-center px-2 lg:px-0">
                            <portal-layout-file-logo v-if="portalContentAndDesignStore.srcRoutes.portal_layout_file_logo" />
                            <div :class="[
                                'flex space-x-4',
                                 navigationLinksStyle === 'border' ? 'h-16' : '',
                                ]"
                            >
                                <navigation-links />
                            </div>
                        </div>

                        <div class="block lg:ml-4">
                            <div class="flex items-center">
                                <mobile-and-desktop-profile-dropdown />
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
            <div class="w-full px-4 pt-8 mx-auto mb-9 max-w-6xl sm:px-8 lg:px-12">
                <header-block
                    heading-classes="font-semibold text-xl lg:text-2xl text-opacity-100"
                    subheading-classes="text-opacity-75 text-sm mt-2"
                />
            </div>
        </template>

        <!-- Portal layout style ONE -->
        <div v-else
            :class="[
                'pb-24',
                sidebarClasses
            ]"
        >
            <nav :class="[
                'hidden lg:block',
                portalContentAndDesignStore.getBorderNavigationColorClass
            ]">
                <div class="px-4 mx-auto max-w-6xl sm:px-8 lg:px-12">
                    <div class="flex relative justify-between items-center -mb-px h-16">
                        <div class="flex items-center px-2 lg:px-0">

                            <portal-layout-file-logo v-if="portalContentAndDesignStore.srcRoutes.portal_layout_file_logo" />

                            <div class="block">
                                <div :class="[
                                    'flex space-x-4',
                                     navigationLinksStyle === 'border' ? 'h-16' : ''
                                ]">
                                    <navigation-links />
                                </div>
                            </div>
                        </div>

                        <div class="block ml-4">
                            <div class="flex items-center">
                                <mobile-and-desktop-profile-dropdown />
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            <div class="px-4 pt-8 mx-auto mb-10 max-w-6xl sm:px-8 lg:px-12">
                <header-block
                    :heading-classes="[
                        'text-xl font-medium lg:text-2xl',
                        navigationColor === 'brand' || navigationColor === 'black' ? 'text-white' : ''
                    ]"
                    :subheading-classes="[
                        'mt-2 text-sm opacity-75',
                        navigationColor === 'brand' || navigationColor === 'black' ? 'text-white' : ''
                    ]"
                />
            </div>
        </div>
    </hamburger>
</template>
